import React, { useEffect, useMemo, useState } from "react";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  FileText,
  Info,
  LogOut,
  MapPin,
  ScrollText,
  Shield,
  Square,
  Trash2,
} from "lucide-react";
import Security from "./Security/Security";
import Notification from "./Notification/Notification";
import DeleteAccount from "./DeleteAccount/DeleteAccount";
import Logout from "./Logout/Logout";
import PrivacyPolicy from "./PrivacyPolicy/PrivacyPolicy";
import TermService from "./TermService/TermService";
import Contract from "./Contract/Contract";
import ConnectSquare from "./ConnectSquare/ConnectSquare";
import NewCredit from "./NewCredit/NewCredit";
import AppVersion from "./AppVersion/AppVersion";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { hitGetProfileApi } from "../../../../Redux/Professional/GetProfileSlice";
import profile_icon from "../../../../assets/img/profileIcon.png";

const APP_VERSION =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_VERSION) ||
  "0.1.0";

const DETAIL_TITLES = {
  square: "Square Connect",
  security: "Password & Security",
  contract: "Contract Settings",
  notifications: "Notifications",
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  credits: "Credits",
  version: "App Version",
  delete: "Delete Account",
};

const SETTINGS_SECTIONS = [
  {
    label: "PAYMENT METHODS",
    items: [
      {
        id: "square",
        title: "Square Connect",
        subtitle: "Manage your Square account",
        Icon: Square,
        action: "detail",
      },
    ],
  },
  {
    label: "ACCOUNT",
    items: [
      {
        id: "security",
        title: "Password & Security",
        subtitle: "Manage your password and security",
        Icon: Shield,
        action: "detail",
      },
      {
        id: "contract",
        title: "Contract Settings",
        subtitle: "Set Contract details",
        Icon: Shield,
        action: "detail",
      },
    ],
  },
  {
    label: "PREFERENCES",
    items: [
      {
        id: "notifications",
        title: "Notifications",
        subtitle: "Push notifications and email alerts",
        Icon: Bell,
        action: "detail",
      },
    ],
  },
  {
    label: "SERVICE AREAS",
    items: [
      {
        id: "serviceArea",
        title: "Add Service Area",
        subtitle: "Manage service areas",
        Icon: MapPin,
        action: "navigate",
        to: "/professional/service-areas",
      },
    ],
  },
  {
    label: "LEGAL",
    items: [
      {
        id: "privacy",
        title: "Privacy Policy",
        subtitle: "How we handle your data",
        Icon: FileText,
        action: "detail",
      },
      {
        id: "terms",
        title: "Terms of Service",
        subtitle: "How we handle your services",
        Icon: ScrollText,
        action: "detail",
      },
    ],
  },
  {
    label: "APP INFO",
    items: [
      {
        id: "versionInfo",
        title: "App Version",
        subtitle: `${APP_VERSION} - Latest Version`,
        Icon: Info,
        action: "static",
        showChevron: false,
      },
    ],
  },
  {
    label: "ACCOUNT MANAGEMENT",
    items: [
      {
        id: "delete",
        title: "Delete Account",
        subtitle: "Permanently delete your account and all data",
        Icon: Trash2,
        action: "detail",
        variant: "danger",
      },
    ],
  },
];

const VALID_DETAIL_IDS = new Set([
  "square",
  "credits",
  "security",
  "notifications",
  "privacy",
  "terms",
  "contract",
  "version",
  "delete",
]);

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const profileResponse = useSelector(
    (state) => state.getProfileProfessionalReducer.data,
  );
  const profileUser =
    profileResponse?.status === true ? profileResponse.data : null;

  useEffect(() => {
    dispatch(hitGetProfileApi());
  }, [dispatch]);

  const profileDisplayName =
    [profileUser?.firstName, profileUser?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    profileUser?.companyName ||
    "Professional";

  const profileImageSrc =
    profileUser?.profileImage ||
    profileUser?.companyLogo ||
    profile_icon;

  const initialDetailId = useMemo(() => {
    const raw = location.state && location.state.type;
    if (typeof raw !== "string" || !raw) return null;
    return raw;
  }, [location.state]);

  const [detailId, setDetailId] = useState(
    initialDetailId && VALID_DETAIL_IDS.has(initialDetailId)
      ? initialDetailId
      : null
  );
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const contentMap = {
    credits: <NewCredit />,
    security: <Security />,
    notifications: <Notification />,
    privacy: <PrivacyPolicy />,
    terms: <TermService />,
    contract: <Contract />,
    version: <AppVersion />,
    delete: <DeleteAccount />,
  };

  useEffect(() => {
    if (initialDetailId && VALID_DETAIL_IDS.has(initialDetailId)) {
      setDetailId(initialDetailId);
    }
  }, [initialDetailId]);

  useEffect(() => {
    if (detailId && !VALID_DETAIL_IDS.has(detailId)) {
      setDetailId(null);
    }
  }, [detailId]);

  const detailContent =
    detailId && VALID_DETAIL_IDS.has(detailId)
      ? contentMap[detailId]
      : null;

  const handleListBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/professional/dashboard");
    }
  };

  const handleRowActivate = (item) => {
    if (item.action === "static") return;
    if (item.action === "navigate" && item.to) {
      navigate(item.to);
      return;
    }
    if (item.action === "detail" && item.id) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setDetailId(item.id);
    }
  };

  const renderRow = (item) => {
    const Icon = item.Icon;
    const isDanger = item.variant === "danger";
    const showChevron = item.showChevron !== false;
    const iconWrapClass = [
      "pro-settings__row-icon-wrap",
      isDanger ? "pro-settings__row-icon-wrap--danger" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const titleClass = [
      "pro-settings__row-title",
      isDanger ? "pro-settings__row-title--danger" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const body = (
      <>
        <span className={iconWrapClass}>
          <Icon
            className="pro-settings__row-icon"
            size={20}
            strokeWidth={2}
          />
        </span>
        <span className="pro-settings__row-text">
          <span className={titleClass}>{item.title}</span>
          <span className="pro-settings__row-subtitle">{item.subtitle}</span>
        </span>
        {showChevron ? (
          <ChevronRight
            className="pro-settings__row-chevron"
            size={20}
            strokeWidth={2}
            aria-hidden
          />
        ) : (
          <span className="pro-settings__row-chevron-spacer" aria-hidden />
        )}
      </>
    );

    if (item.action === "static") {
      return (
        <div
          className="pro-settings__row pro-settings__row--static"
          role="status"
        >
          {body}
        </div>
      );
    }

    return (
      <button
        type="button"
        className={[
          "pro-settings__row",
          isDanger ? "pro-settings__row--danger" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => handleRowActivate(item)}
      >
        {body}
      </button>
    );
  };

  if (detailId === "square") {
    return (
      <>
        <ConnectSquare
          onBack={() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
            setDetailId(null);
          }}
        />
        {showLogoutDialog && (
          <Logout onCancel={() => setShowLogoutDialog(false)} />
        )}
      </>
    );
  }

  if (detailId && detailContent) {
    return (
      <>
      <div className="pro-settings pro-settings--detail">
        <header className="pro-settings__header">
          <button
            type="button"
            className="pro-settings__header-btn"
            aria-label="Back to settings"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              setDetailId(null);
            }}
          >
            <ChevronLeft size={22} strokeWidth={2} />
          </button>
          <h1 className="pro-settings__header-title">
            {DETAIL_TITLES[detailId] || "Settings"}
          </h1>
        </header>
        <div className="pro-settings__divider" />
        <div className="pro-settings__detail-body">{detailContent}</div>
      </div>
      {showLogoutDialog && (
        <Logout onCancel={() => setShowLogoutDialog(false)} />
      )}
      </>
    );
  }

  return (
    <>
    <div className="pro-settings pro-settings--list">
      <header className="pro-settings__header">
        <button
          type="button"
          className="pro-settings__header-btn"
          aria-label="Go back"
          onClick={handleListBack}
        >
          <ChevronLeft size={22} strokeWidth={2} />
        </button>
        <h1 className="pro-settings__header-title">Settings</h1>
      </header>
      <div className="pro-settings__divider" />

      <div className="pro-settings__profile-card">
        <img
          src={profileImageSrc}
          alt={profileDisplayName}
          className="pro-settings__profile-avatar"
          onError={(e) => {
            e.currentTarget.src = profile_icon;
          }}
        />
        <span className="pro-settings__profile-name">{profileDisplayName}</span>
      </div>

      <div className="pro-settings__scroll">
        {SETTINGS_SECTIONS.map((section) => (
          <section key={section.label} className="pro-settings__section">
            <h2 className="pro-settings__section-label">{section.label}</h2>
            <ul className="pro-settings__card-list">
              {section.items.map((item) => (
                <li key={item.id}>{renderRow(item)}</li>
              ))}
            </ul>
          </section>
        ))}

        <div className="pro-settings__footer-actions">
          <button
            type="button"
            className="pro-settings__sign-out"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              setShowLogoutDialog(true);
            }}
          >
            <LogOut size={20} strokeWidth={2} aria-hidden />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
    {showLogoutDialog && (
      <Logout onCancel={() => setShowLogoutDialog(false)} />
    )}
    </>
  );
};

export default Sidebar;
